/***
*
*   ALERTS
*   Full page view of all system alerts with filtering
*
**********/

import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Card, Table, Tag, Select, Space, Typography, Row, Col,
  Statistic, Button, Tooltip
} from 'antd';
import {
  AlertOutlined, CloudServerOutlined, ClockCircleOutlined,
  CalendarOutlined, CreditCardOutlined, UserOutlined,
  ProjectOutlined, ExclamationCircleOutlined, EyeOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { fetchAlerts, invalidateAlertsCache } from '../store/slices/alertsSlice';
import { getCacheKey } from '../store/cacheUtils';
import { Animate, usePermissions } from 'components/lib';

const { Title, Text } = Typography;
const { Option } = Select;

export function Alerts() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { can } = usePermissions();

  const [filters, setFilters] = useState({
    type: '',
    severity: '',
    page: 1,
    limit: 20
  });

  const alertsState = useSelector((state) => state.alerts);
  const cacheKey = getCacheKey(filters);

  useEffect(() => {
    dispatch(fetchAlerts(filters));
  }, [dispatch, filters]);

  const alertsData = alertsState.data[cacheKey] || {};
  const { alerts: alertsList = [], summary = {} } = alertsData;

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: key !== 'page' ? 1 : value
    }));
  };

  const handleRefresh = () => {
    console.log('In')
    dispatch(invalidateAlertsCache());
    dispatch(fetchAlerts(filters));
  };

  const getAlertIcon = (type) => {
    const icons = {
      storage: <CloudServerOutlined />,
      trial_expiring: <ClockCircleOutlined />,
      trial_expired: <CalendarOutlined />,
      payment_failed: <CreditCardOutlined />,
      employee_limit: <UserOutlined />,
      job_opening_limit: <ProjectOutlined />,
    };
    return icons[type] || <ExclamationCircleOutlined />;
  };

  const getSeverityColor = (severity) => {
    if (!severity) return 'default';
    if (severity === 'high') return 'red';
    if (severity === 'medium') return 'orange';
    if (severity === 'low') return 'blue';
    // const colors = { critical: 'red', warning: 'orange', info: 'blue' };
    // return colors[severity] || 'default';
  };

  const columns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => (
        <Space>
          {getAlertIcon(type)}
          <Text>{type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</Text>
        </Space>
      ),
    },
    {
      title: 'Workspace',
      dataIndex: 'workspace_name',
      key: 'workspace_name',
      render: (name, record) => <Text strong>{name || record.message?.split('"')[1] || 'N/A'}</Text>,
    },
    {
      title: 'Severity',
      dataIndex: 'severity',
      key: 'severity',
      render: (severity) => (
        <Tag color={getSeverityColor(severity)}>
          {severity?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
      ellipsis: true,
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date) => date ? new Date(date).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
      }) : 'N/A',
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        can('workspaces', 'view') && (
          <Tooltip title="View Workspace">
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/workspaces/${record.workspace_id}`)}
              disabled={!record.workspace_id}
            >
              View
            </Button>
          </Tooltip>
        )
      ),
    },
  ];

  const alertTypes = summary.by_type ? Object.keys(summary.by_type) : [];

  return (
    <Animate type="pop">
      <div>
        {/* Header */}
        <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={3} style={{ margin: 0 }}>
              <AlertOutlined style={{ marginRight: 8 }} />
              System Alerts
            </Title>
            <Text type="secondary">Monitor and manage system-wide alerts</Text>
          </div>
          <Button icon={<ReloadOutlined />} onClick={handleRefresh}>Refresh</Button>
        </div>

        {/* Summary Stats */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Total Alerts"
                value={summary.total || 0}
                prefix={<AlertOutlined style={{ color: '#7c3aed' }} />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Critical"
                value={summary?.high || 0}
                valueStyle={{ color: '#ff4d4f' }}
                prefix={<ExclamationCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Warnings"
                value={summary?.medium || 0}
                valueStyle={{ color: '#fa8c16' }}
                prefix={<ExclamationCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={6}>
            <Card>
              <Statistic
                title="Low Severity"
                value={summary?.low || 0}
                valueStyle={{ color: '#1890ff' }}
                prefix={<ExclamationCircleOutlined />}
              />
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Card style={{ marginBottom: 16 }}>
          <Space>
            <Select
              placeholder="All Types"
              allowClear
              style={{ width: 180 }}
              value={filters.type || undefined}
              onChange={(val) => handleFilterChange('type', val || '')}
            >
              {alertTypes.map(type => (
                <Option key={type} value={type}>
                  {type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </Option>
              ))}
            </Select>
            <Select
              placeholder="All Severities"
              allowClear
              style={{ width: 160 }}
              value={filters.severity || undefined}
              onChange={(val) => handleFilterChange('severity', val || '')}
            >
              <Option value="high">High</Option>
              <Option value="medium">Medium</Option>
              <Option value="low">Low</Option>
            </Select>
          </Space>
        </Card>

        {/* Table */}
        <Card>
          <Table
            columns={columns}
            dataSource={alertsList}
            rowKey={(record, index) => record.workspace_id ? `${record.workspace_id}-${index}` : `alert-${index}`}
            loading={alertsState.loading}
            pagination={{
              current: filters.page,
              pageSize: filters.limit,
              total: summary.total || 0,
              onChange: (page) => handleFilterChange('page', page),
              showSizeChanger: false,
            }}
          />
        </Card>
      </div>
    </Animate>
  );
}